import { fg } from '@atlaskit/platform-feature-flags/fg';
import { conversationAssistantChatPromptInputI18n } from '@atlassian/conversation-assistant-chat-prompt-input/i18n/conversation-assistant-chat-prompt-input-i18n';
import { conversationAssistantMessageActionsI18n } from '@atlassian/conversation-assistant-message-actions/i18n/conversation-assistant-message-actions-i18n';
import { conversationAssistantUiComponentsI18n } from '@atlassian/conversation-assistant-ui-components/i18n/conversation-assistant-ui-components-i18n';

import defaultMessages from './en';

type I18nMessages = Record<string, string>;
type I18nMessagesModule = { default: I18nMessages };

const conversationAssistantAsyncLanguages = {
	cs: (): Promise<typeof import('./cs')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/cs" */ './cs'),
	da: (): Promise<typeof import('./da')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/da" */ './da'),
	de: (): Promise<typeof import('./de')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/de" */ './de'),
	'en-GB': (): Promise<typeof import('./en_GB')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/en_GB" */ './en_GB'),
	'en-ZZ': (): Promise<typeof import('./en_ZZ')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/en_ZZ" */ './en_ZZ'),
	en: (): Promise<typeof import('./en')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/en" */ './en'),
	es: (): Promise<typeof import('./es')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/es" */ './es'),
	fi: (): Promise<typeof import('./fi')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/fi" */ './fi'),
	fr: (): Promise<typeof import('./fr')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/fr" */ './fr'),
	hr: (): Promise<typeof import('./hr')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/hr" */ './hr'),
	hu: (): Promise<typeof import('./hu')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/hu" */ './hu'),
	it: (): Promise<typeof import('./it')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/it" */ './it'),
	ja: (): Promise<typeof import('./ja')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/ja" */ './ja'),
	ko: (): Promise<typeof import('./ko')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/ko" */ './ko'),
	nb: (): Promise<typeof import('./nb')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/nb" */ './nb'),
	nl: (): Promise<typeof import('./nl')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/nl" */ './nl'),
	pl: (): Promise<typeof import('./pl')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/pl" */ './pl'),
	'pt-BR': (): Promise<typeof import('./pt_BR')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/pt_BR" */ './pt_BR'),
	ru: (): Promise<typeof import('./ru')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/ru" */ './ru'),
	sl: (): Promise<typeof import('./sl')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/sl" */ './sl'),
	'sr-RS': (): Promise<typeof import('./sr_RS')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/sr_RS" */ './sr_RS'),
	'sr-YR': (): Promise<typeof import('./sr_YR')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/sr_YR" */ './sr_YR'),
	sv: (): Promise<typeof import('./sv')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/sv" */ './sv'),
	th: (): Promise<typeof import('./th')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/th" */ './th'),
	tr: (): Promise<typeof import('./tr')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/tr" */ './tr'),
	uk: (): Promise<typeof import('./uk')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/uk" */ './uk'),
	vi: (): Promise<typeof import('./vi')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/vi" */ './vi'),
	'zh-TW': (): Promise<typeof import('./zh_TW')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/zh_TW" */ './zh_TW'),
	zh: (): Promise<typeof import('./zh')> =>
		import(/* webpackChunkName: "@atlassian/conversation-assistant/i18n/zh" */ './zh'),
} as const;

type AsyncLanguageLoaders = Record<
	keyof typeof conversationAssistantAsyncLanguages,
	() => Promise<I18nMessagesModule>
>;

const defaultMessagesWithDependencies: I18nMessages = {
	...conversationAssistantChatPromptInputI18n.defaultMessages,
	...conversationAssistantUiComponentsI18n.defaultMessages,
	...defaultMessages,
};

const mergeLocaleMessages = (localeMessages: I18nMessages[]): I18nMessagesModule => ({
	default: localeMessages.reduce<I18nMessages>(
		(mergedMessages, messages) => Object.assign(mergedMessages, messages),
		{},
	),
});

/**
 * Merges the bundles in ascending order of precedence. A single failed chunk fetch rejects the
 * whole locale.
 */
const combineLocaleMessages = async (
	localeMessagesPromises: Promise<I18nMessagesModule>[],
): Promise<I18nMessagesModule> =>
	mergeLocaleMessages((await Promise.all(localeMessagesPromises)).map(({ default: it }) => it));

/**
 * Merges every bundle that loaded, in ascending order of precedence. A dependency whose chunk
 * fails to fetch loses only its own messages instead of taking the whole locale down with it.
 */
const combineAvailableLocaleMessages = async (
	localeMessagesPromises: Promise<I18nMessagesModule>[],
): Promise<I18nMessagesModule> =>
	mergeLocaleMessages(
		(await Promise.allSettled(localeMessagesPromises)).flatMap((result) =>
			result.status === 'fulfilled' ? [result.value.default] : [],
		),
	);

const createCombinedLocaleLoader =
	(locale: keyof typeof conversationAssistantAsyncLanguages): (() => Promise<I18nMessagesModule>) =>
	() => {
		if (fg('rovo_chat_message_actions_i18n')) {
			return combineAvailableLocaleMessages([
				conversationAssistantChatPromptInputI18n.asyncLanguages[locale](),
				conversationAssistantUiComponentsI18n.asyncLanguages[locale](),
				conversationAssistantMessageActionsI18n.asyncLanguages[locale](),
				conversationAssistantAsyncLanguages[locale](),
			]);
		}

		return combineLocaleMessages([
			conversationAssistantChatPromptInputI18n.asyncLanguages[locale](),
			conversationAssistantUiComponentsI18n.asyncLanguages[locale](),
			conversationAssistantAsyncLanguages[locale](),
		]);
	};

const asyncLanguages: AsyncLanguageLoaders = {
	cs: createCombinedLocaleLoader('cs'),
	da: createCombinedLocaleLoader('da'),
	de: createCombinedLocaleLoader('de'),
	'en-GB': createCombinedLocaleLoader('en-GB'),
	'en-ZZ': createCombinedLocaleLoader('en-ZZ'),
	en: createCombinedLocaleLoader('en'),
	es: createCombinedLocaleLoader('es'),
	fi: createCombinedLocaleLoader('fi'),
	fr: createCombinedLocaleLoader('fr'),
	hr: createCombinedLocaleLoader('hr'),
	hu: createCombinedLocaleLoader('hu'),
	it: createCombinedLocaleLoader('it'),
	ja: createCombinedLocaleLoader('ja'),
	ko: createCombinedLocaleLoader('ko'),
	nb: createCombinedLocaleLoader('nb'),
	nl: createCombinedLocaleLoader('nl'),
	pl: createCombinedLocaleLoader('pl'),
	'pt-BR': createCombinedLocaleLoader('pt-BR'),
	ru: createCombinedLocaleLoader('ru'),
	sl: createCombinedLocaleLoader('sl'),
	'sr-RS': createCombinedLocaleLoader('sr-RS'),
	'sr-YR': createCombinedLocaleLoader('sr-YR'),
	sv: createCombinedLocaleLoader('sv'),
	th: createCombinedLocaleLoader('th'),
	tr: createCombinedLocaleLoader('tr'),
	uk: createCombinedLocaleLoader('uk'),
	vi: createCombinedLocaleLoader('vi'),
	'zh-TW': createCombinedLocaleLoader('zh-TW'),
	zh: createCombinedLocaleLoader('zh'),
};

export const conversationAssistantI18n: {
	defaultMessages: I18nMessages;
	asyncLanguages: typeof asyncLanguages;
} = {
	defaultMessages: defaultMessagesWithDependencies,
	asyncLanguages: asyncLanguages,
};
